import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Search as SearchIcon } from "@mui/icons-material";
import baseURL from "../../config";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

const DepartureModal = ({ departureDetails }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [openPdfModal, setOpenPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [filteredDepartureDetails, setFilteredDepartureDetails] = useState([]);
  const [loadingCrewId, setLoadingCrewId] = useState(null);
  const [dgChargesData, setDgChargesData] = useState(null);
  const [openChargesDialog, setOpenChargesDialog] = useState(false);

  const fetchDGCharges = async (contractId) => {
    try {
      const token = localStorage.getItem("token"); // or your auth logic
      const response = await axios.post(
        `${baseURL}/api/chargesDGSignOnSignOff/${contractId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setDgChargesData(response.data);
      setOpenChargesDialog(true);
    } catch (error) {
      console.error("Error fetching DG Charges:", error);
      toast.error("Failed to load DG Charges.");
    }
  };


  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleSendRequest = async (crewId) => {
    console.log("Send Request triggered for Crew ID:", crewId);
    if (!crewId) return alert("Crew ID not found.");

    setLoadingCrewId(crewId);

    try {
      const res = await fetch(
        `${baseURL}/api/services/send-sea-service-request/${crewId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ crewId }),
        }
      );

      if (!res.ok) throw new Error("Failed to send email.");
      toast.success("Sea Service request email sent successfully.");
    } catch (err) {
      console.error(err);
      alert("Error sending email.");
    } finally {
      setLoadingCrewId(null); // Reset loading state
    }
  };

  // Filter on search
  useEffect(() => {
    if (Array.isArray(departureDetails)) {
      const filtered = departureDetails.filter((item) => {
        const portName =
          item.contractStatusAfterForm1?.departure?.[0]?.portName?.toLowerCase() || "";
        return portName.includes(searchQuery);
      });
      setFilteredDepartureDetails(filtered);
    } else {
      setFilteredDepartureDetails([]);
    }
  }, [departureDetails, searchQuery]);

  // Check for approved sea service
  useEffect(() => {
    const checkApprovedSeaService = async () => {
      const updatedDetails = await Promise.all(
        filteredDepartureDetails.map(async (item) => {
          try {
            const contractId = item.contractDetails?._id;
            const response = await axios.get(
              `${baseURL}/api/services/getApprovedSeaserviceByContractId/${contractId}`
            );
            return {
              ...item,
              approvedSeaService: response.data?.approved === true,
            };
          } catch (error) {
            console.error("Error fetching approved sea service:", error);
            return { ...item, approvedSeaService: false };
          }
        })
      );
      setFilteredDepartureDetails(updatedDetails);
    };

    if (filteredDepartureDetails.length > 0) {
      checkApprovedSeaService();
    }
  }, [filteredDepartureDetails.length]);

  return (
    <Box mt={3} width="100%" p={2}>
      {/* Search Bar & Title */}
      <Box
        sx={{
          p: 1,
          bgcolor: "#C30B12",
          color: "white",
          display: "flex",
          alignItems: "center",
          position: "relative",
        }}
      >
        <TextField
          placeholder="Search by Port Name"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{
            bgcolor: "#f5f5f5",
            borderRadius: 2,
            width: "280px",
          }}
        />

        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
          }}
        >
          Arrival Details
        </Typography>
      </Box>

      {/* Table Section */}
      <Paper elevation={3} sx={{ p: 2 }}>
        {filteredDepartureDetails.length > 0 ? (
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Sr. No.</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Departure Details
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", width: 150 }}>
                  CrewWelfare
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", width: 250 }}>
                  Sea Service Request
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDepartureDetails.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      <strong style={{ color: "black" }}>
                        Vessel Name:
                      </strong>{" "}
                      {item.contractDetails?.ShipDetails?.vesselname || "N/A"}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        marginTop: 1,
                      }}
                    >
                      <Chip
                        size="small"
                        label={`Crew: ${item.contractDetails?.crewdetails?.name || "N/A"
                          }`}
                        sx={{ bgcolor: "#e3f2fd", color: "#0d47a1" }}
                      />
                      <Chip
                        size="small"
                        label={`Indos No.: ${item.contractDetails?.crewdetails?.indosno || "N/A"
                          }`}
                        sx={{ bgcolor: "#fff9c4", color: "#f57f17" }}
                      />
                      <Chip
                        size="small"
                        label={`Agent.: ${item.contractDetails?.crewingAgent?.agentName || "N/A"
                          }`}
                        sx={{ bgcolor: "#fff9c4", color: "#f57f17" }}
                      />
                      <Chip
                        size="small"
                        label={`Port Name: ${item.contractStatusAfterForm1?.departure?.[0]?.portName ||
                          "N/A"
                          }`}
                        sx={{ bgcolor: "#e8f5e9", color: "#1b5e20" }}
                      />
                      <Chip
                        size="small"
                        label={`Arrival Date: ${item.contractStatusAfterForm1?.departure?.[0]?.date
                          ? new Date(
                            item.contractStatusAfterForm1.departure[0].date
                          ).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                          : "N/A"
                          }`}
                        sx={{ bgcolor: "#fff3e0", color: "#e65100" }}
                      />
                      {item.approvedSeaService ? (
                        <Chip
                          label="Approved Searvice Request"
                          sx={{
                            bgcolor: "#c8e6c9",
                            color: "#2e7d32",
                            fontWeight: "bold",
                          }}
                        />
                      ) : (
                        <Chip
                          label="Not Approved Searvice Request"
                          sx={{
                            bgcolor: "#ffcdd2",
                            color: "#c62828",
                            fontWeight: "bold",
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => fetchDGCharges(item.contractDetails?._id)}
                    >
                      <AttachMoneyIcon />
                    </IconButton>
                  </TableCell>

                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <button
                        style={{
                          backgroundColor: "#1976d2",
                          color: "white",
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          const crewId =
                            item.contractDetails?.crewdetails?._id;
                          if (crewId) {
                            setPdfUrl(
                              `${baseURL}/api/pdf/sea-service/${crewId}`
                              // `http://localhost:8000/api/pdf/sea-service/${crewId}`

                            );
                            setOpenPdfModal(true);
                          } else {
                            alert("Crew ID not found.");
                          }
                        }}
                      >
                        View
                      </button>
                      <Button
                        variant="outlined"
                        disabled={loadingCrewId === item.contractDetails?.crewdetails?._id}
                        onClick={() =>
                          handleSendRequest(item.contractDetails?.crewdetails?._id)
                        }
                        startIcon={
                          loadingCrewId === item.contractDetails?.crewdetails?._id ? (
                            <CircularProgress size={16} />
                          ) : null
                        }
                      >
                        {loadingCrewId === item.contractDetails?.crewdetails?._id
                          ? "Sending..."
                          : "Send Request"}
                      </Button>

                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography color="textSecondary" align="center">
            No departure details found.
          </Typography>
        )}
      </Paper>

      <Dialog
        open={openPdfModal}
        onClose={() => setOpenPdfModal(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            height: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle>
          Sea Service PDF
          <IconButton
            aria-label="close"
            onClick={() => setOpenPdfModal(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, flexGrow: 1, overflow: "hidden" }}>
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="PDF Viewer"
          />
        </DialogContent>
      </Dialog>

      <Dialog
  open={openChargesDialog}
  onClose={() => setOpenChargesDialog(false)}
  fullWidth
  maxWidth="xs"
>
  <DialogTitle>
    DG Charges Details
    <IconButton
      aria-label="close"
      onClick={() => setOpenChargesDialog(false)}
      sx={{
        position: "absolute",
        right: 8,
        top: 8,
        color: (theme) => theme.palette.grey[500],
      }}
    >
      <CloseIcon />
    </IconButton>
  </DialogTitle>
  <DialogContent dividers>
    {dgChargesData ? (
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="body1">
          <strong>Sign On Date:</strong>{" "}
          {new Date(dgChargesData.signOnDate).toLocaleDateString("en-GB")}
        </Typography>
        <Typography variant="body1">
          <strong>Sign Off Date:</strong>{" "}
          {new Date(dgChargesData.signOffDate).toLocaleDateString("en-GB")}
        </Typography>
        <Typography variant="h6" color="primary">
          <strong>Total DG Charges:</strong> ₹ {dgChargesData.exactTotal}
        </Typography>
      </Box>
    ) : (
      <Typography>No charges data available.</Typography>
    )}
  </DialogContent>
</Dialog>

      <ToastContainer />
    </Box>
  );
};

export default DepartureModal;
